<script lang="ts">
  import AddPromotion from "@frontend/Components/AddPromotion.svelte";
import Modal from "@frontend/Components/Modal.svelte";
  import { Promotionalcredits, RefreshCampaigns } from "@frontend/PromotionalCredits";
  import Pagination from "@frontend/Components/Pagination.svelte";
  import { push } from "svelte-spa-router";
  import { formatMoney } from "./Formats";
  import { CampaignsDTO, EditCampaignsDTO } from "@frontend/walletapi";
  import EditCampaign from "@frontend/Components/EditCampaign.svelte";
  import { me } from "@frontend/user";
  import { onMount } from "svelte";
  import { translate, currentLanguage } from "../Translator";

    type EditAction = {
    action: "edit",
    promotionalcreditsid: number;
  };
   
  type AddAction = {
    action: "add"
  };

  type RouteParams =
    EditAction |
    AddAction |
    undefined;

  export let params: RouteParams = undefined;

    let selectedCampaign: CampaignsDTO | undefined = undefined;
    let currentPage = 1;
    const pageSize = 20;
    $: visibleCampaigns = $Promotionalcredits.slice((currentPage - 1) * pageSize, currentPage * pageSize);
    $: if (params) {
        if (params.action == "edit") {
            let selectedCampaignId = params.promotionalcreditsid;
            selectedCampaign = $Promotionalcredits.find(s => s.Id == +selectedCampaignId);
        }
    }

    // Función para convertir timespan a formato legible y amigable
    function formatTimespan(timespan: string): string {
        if (!timespan || timespan === "0") return `⏰ ${$translate('Undefined')}`;
        
        // Parsear el formato timespan (ej: "1.00:00:00" o "01:00:00")
        const parts = timespan.split(':');
        let days = 0;
        let hours = 0;
        let minutes = 0;
        let seconds = 0;
        
        if (parts.length === 3) {
            // Formato: "HH:MM:SS" o "DD.HH:MM:SS"
            if (parts[0].includes('.')) {
                // Formato con días: "DD.HH:MM:SS"
                const dayHourParts = parts[0].split('.');
                days = parseInt(dayHourParts[0]) || 0;
                hours = parseInt(dayHourParts[1]) || 0;
            } else {
                // Formato sin días: "HH:MM:SS"
                days = 0;
                hours = parseInt(parts[0]) || 0;
            }
            minutes = parseInt(parts[1]) || 0;
            seconds = parseInt(parts[2]) || 0;
        }
        
        // Construir el texto detallado con todos los componentes
        const partsText = [];
        
        if (days > 0) {
            const dayText = days > 1 ? $translate('days') : $translate('day');
            partsText.push(`${days} ${dayText}`);
        }
        
        if (hours > 0) {
            const hourText = hours > 1 ? $translate('hours') : $translate('hour');
            partsText.push(`${hours} ${hourText}`);
        }
        
        if (minutes > 0) {
            const minuteText = minutes > 1 ? $translate('minutes') : $translate('minute');
            partsText.push(`${minutes} ${minuteText}`);
        }
        
        if (seconds > 0) {
            const secondText = seconds > 1 ? $translate('seconds') : $translate('second');
            partsText.push(`${seconds} ${secondText}`);
        }
        
        // Si no hay ningún valor, mostrar sin duración
        if (partsText.length === 0) {
            return `⏰ ${$translate('Undefined')}`;
        }
        
        // Mostrar en formato: "X días, Y horas, Z minutos, W segundos"
        return partsText.join(', ');
    }

    // Función para formatear fecha de expiración (solo fecha, sin hora)
    function formatExpirationDate(dateString: string | Date | null | undefined): string {
        if (!dateString) return  `📅 ${$translate('No date')}`;
        
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return  `📅 ${$translate('Invalid date')}`;;
            
            // Formato: DD/MM/YYYY
            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear();
            
            return `📅 ${day}/${month}/${year}`;
        } catch {
            return "📅 Error de fecha";
        }
    }

    // Función para formatear los ajustes JSON
    function formatSettings(settings: any): string {
        if (!settings) return `⚙️ ${$translate('No configuration')}`;
        
        try {
            // Si ya es un string JSON, intentar parsearlo
            let parsedSettings;
            if (typeof settings === 'string') {
                parsedSettings = JSON.parse(settings);
            } else {
                parsedSettings = settings;
            }
            
            // Si es un objeto válido, mostrar información específica de promociones
            if (typeof parsedSettings === 'object' && parsedSettings !== null) {
                if (parsedSettings.promotions && Array.isArray(parsedSettings.promotions)) {
                    const promotion = parsedSettings.promotions[0];
                    if (promotion) {
                        if (promotion.type === "Recharge" && promotion.ranges) {
                            const rangeCount = promotion.ranges.length;
                            return `⚙️ ${$translate('Recharge')} (${rangeCount} ${$translate('Ranges')})`;
                        } else if (promotion.fixedAmount !== undefined) {
                            return `⚙️ ${promotion.type} ($${promotion.fixedAmount})`;
                        } else {
                            return `⚙️ ${promotion.type}`;
                        }
                    }
                }
                
                // Fallback para otros tipos de objetos
                const keys = Object.keys(parsedSettings);
                if (keys.length === 0) {
                    return "⚙️ {}";
                }
                
                // Mostrar solo las primeras 2 propiedades para no saturar la tabla
                const preview = keys.slice(0, 2).map(key => {
                    const value = parsedSettings[key];
                    if (typeof value === 'object') {
                        return `${key}: {...}`;
                    }
                    return `${key}: ${value}`;
                }).join(', ');
                
                const more = keys.length > 2 ? ` (+${keys.length - 2} más)` : '';
                return `⚙️ {${preview}${more}}`;
            }
            
            return `⚙️ ${JSON.stringify(parsedSettings)}`;
        } catch (e) {
            // Si no es JSON válido, mostrar como string
            if (typeof settings === 'string') {
                return settings.length > 50 ? `⚙️ ${settings.substring(0, 50)}...` : `⚙️ ${settings}`;
            }
            return `⚙️ ${$translate('Settings error')}`;
        }
    }
    
  function dismiss() {
    push("#/PromotionalCredits");
  }

  onMount(async () => {
    if (!$me) {
      push("/user/Login");
      return;
    }
    await RefreshCampaigns();
  });
</script>

<style>
  .duration-cell {
    font-size: 0.9rem;
    color: #2c3e50;
    font-weight: 500;
    padding: 8px 12px;
    border-radius: 6px;
    background: #ffffff;
    border: 1px solid #e0e0e0;
    text-align: center;
    white-space: nowrap;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
  }

  .duration-cell:hover {
    background: #f8f9fa;
    border-color: #d0d0d0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .duration-cell::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, transparent 30%, rgba(0,0,0,0.02) 50%, transparent 70%);
    transform: translateX(-100%);
    transition: transform 0.6s;
  }

  .duration-cell:hover::before {
    transform: translateX(100%);
  }

  /* Responsive design para móviles */
  @media (max-width: 768px) {
    .duration-cell {
      font-size: 0.8rem;
      padding: 6px 8px;
    }
  }

  /* Animación suave al cargar */
  .duration-cell {
    animation: fadeIn 0.5s ease-out;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  /* Estilos para la tabla en general - formato original con cuadros simples */
  .playerlist {
    border-collapse: collapse;
    width: 100%;
  }

  .playerlist th {
    background: #f8f9fa;
    color: #333;
    font-weight: 600;
    text-align: center;
    padding: 12px 8px;
    border: 1px solid #dee2e6;
  }

  .playerlist td {
    background: #ffffff;
    text-align: center;
    padding: 10px 8px;
    border: 1px solid #dee2e6;
    vertical-align: middle;
  }

  .playerlist td:hover {
    background: #f8f9fa;
  }

  /* Estilos específicos para la celda de duración */
  .duration-cell {
    font-size: 0.9rem;
    color: #2c3e50;
    font-weight: 500;
    padding: 10px 8px;
    background: #ffffff;
    border: 1px solid #dee2e6;
    text-align: center;
    white-space: nowrap;
    vertical-align: middle;
  }

  .duration-cell:hover {
    background: #f8f9fa;
  }

  /* Estilos específicos para la celda de settings */
  .settings-cell {
    font-size: 0.85rem;
    color: #495057;
    font-family: 'Courier New', monospace;
    padding: 8px 10px;
    background: #ffffff;
    border: 1px solid #dee2e6;
    text-align: center;
    white-space: nowrap;
    vertical-align: middle;
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    border-radius: 4px;
  }

  .settings-cell:hover {
    background: #f8f9fa;
    border-color: #adb5bd;
  }

  /* Estilos para el estado activo/inactivo */
  .status-active {
    color: #21ba45 !important;
    font-weight: 600;
  }

  .status-inactive {
    color: #db2828 !important;
    font-weight: 600;
  }

  .status-active i.circle.icon {
    color: #21ba45 !important;
  }

  .status-inactive i.circle.icon {
    color: #db2828 !important;
  }
</style>


{#if params?.action == "add"}
    <Modal dismiss={dismiss}>
      <AddPromotion dismiss={dismiss}></AddPromotion>
    </Modal>
{/if}

{#if params?.action == "edit"}
    <Modal dismiss={dismiss}>
        {#if !selectedCampaign}
            <div class="ui negative message">
                <div class="header">No such Campaign</div>
            </div>
        {:else}
            <EditCampaign campaign={selectedCampaign} dismiss={dismiss} />
        {/if}
    </Modal>
{/if}



<h1 class="ui centered dividing header">{$translate('Promotional List')}</h1>
{#if $me?.Role && $me.Role.toLowerCase() === 'owner'}
<a href="#/PromotionalCredits/add" class="ui green button">
  <i class="plus icon"></i>
  {$translate('Add Promo')}
</a>
{/if}


<table class="playerlist ui celled unstackable striped selectable very compact unselectable table">
  <thead>
    <tr>
      <th style="width: 0px">#</th>
      <th>{$translate('Type')}</th>
      <th>{$translate('Promotion')}</th>
      <th>{$translate('Amount')}</th>
      <th>{$translate('Active')}</th>
      <th>{$translate('Total Days')}</th>
      <th>{$translate('Expiration Date')}</th>
      <th>Settings</th>
      <th style="width: 0px">{$translate('Options')}</th>
    </tr>
  </thead>
  <tbody>
    {#each visibleCampaigns ?? [] as pc}
      <tr>
        <td class="numeric">{pc.Id}</td>
        <td>{pc.Type ?? ""}</td>
        <td>{pc.PromotionName ?? ""}</td>
        <td>{formatMoney(pc.StartAmount)}</td>
        <!-- <td>{pc.Active ? "Activo" : "Inactivo"}</td> -->
        <td>
          {#if pc.Active}
            <span class="status-active">
              <i class="circle icon"></i> {$translate('Active')}
            </span>
          {:else}
            <span class="status-inactive">
              <i class="circle icon"></i> {$translate('Inactive')}
            </span>
          {/if}
        </td>
        <td class="duration-cell">{formatTimespan(pc.TotalDays)}</td>
        <td>{formatExpirationDate(pc.ExpirationDate)}</td>
        <td class="settings-cell">{formatSettings(pc.Settings)}</td>
        <td style="white-space: nowrap;">
            <a  href={`#/PromotionalCredits/edit/${pc.Id}`} class="ui icon primary button">
                <i class="edit icon"></i>
            </a>
        </td>
      </tr>
    {/each}     
  </tbody>
</table>
<Pagination page={currentPage} totalItems={$Promotionalcredits.length} {pageSize} on:change={(event) => currentPage = event.detail} />
